import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";

import { partyAuthenticationManager } from "../../../../shared/core/service/services";

export enum STATUS {
  STARTED = "STARTED",
  PENDING = "PENDING",
  DONE = "DONE",
  REFUSED = "REFUSED",
  CANCELLED = "CANCELLED",
  FAILED = "FAILED",
}

interface SessionPolling {
  status: string;
  error: string;
  expiredAt: string;
}

export const usePollingStateSession = (
  sessionToken: string | undefined,
  dismissPollingSessionStateModal: () => void,
) => {
  const [sessionPolling, setSessionPolling] = useState<SessionPolling | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [hasSessionPolling, setHasSessionPolling] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const expirationDateModal = moment().add(15, "minutes");
  const [error, setError] = useState();

  const fetchSessionPolling = useCallback(async () => {
    if (!sessionToken) return;

    try {
      const session = await partyAuthenticationManager.getSession(sessionToken);
      if (session) {
        if (moment().isBefore(expirationDateModal)) {
          actionsSessionByStatus(session);
        } else {
          setIsExpired(true);
          clearPolling();
        }
      }
    } catch (e) {
      setError(new Error("Session does not exist"));
    }
  }, [sessionToken]);

  const actionsSessionByStatus = (session: SessionPolling) => {
    // Check if it has been expired or not
    if (session.expiredAt || session.status === STATUS.STARTED || session.status === STATUS.PENDING) {
      checkDateSessionExpired(session);
    }
    if (session.status === STATUS.REFUSED) {
      clearPolling();
    }
    if (session.status === STATUS.CANCELLED) {
      clearPolling();
    }
    if (session.status === STATUS.DONE) {
      clearPolling();
      dismissPollingSessionStateModal();
    }
  };

  const checkDateSessionExpired = (session: SessionPolling) => {
    const dateToCheck = new Date(session.expiredAt);
    const now = new Date();
    if (dateToCheck > now) {
      // Session is not expired
      setSessionPolling(session);
      if (session.status === STATUS.STARTED || session.status === STATUS.PENDING) {
        setHasSessionPolling(true);
      } else {
        setHasSessionPolling(false);
      }
    } else {
      // Session is expired
      setIsExpired(true);
    }
  };

  const clearPolling = () => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (!sessionToken) return;

    // Start polling every 3 seconds
    intervalRef.current = setInterval(fetchSessionPolling, 3000);

    return () => {
      clearPolling(); // cleanup on unmount
    };
  }, []);

  return {
    sessionPolling,
    hasSessionPolling,
    isExpired,
    isDone: sessionPolling?.status === STATUS.DONE,
    isStarted: sessionPolling?.status === STATUS.STARTED,
    isPending: sessionPolling === null || sessionPolling?.status === STATUS.PENDING,
    error,
  };
};
